/**
 * MARCA | 21/08/2026 | Product page bundle builder web component.
 * Renders a tier-based bundle upsell (1/2/3 bottles) with accordion-expanding
 * fragrance selectors. Listens to variant changes via PUB_SUB_EVENTS to sync
 * the selected size, fetches products via section rendering, and submits
 * bundles to cart via /cart/add.js.
 */
if (!customElements.get('product-bundle-builder')) {
  customElements.define(
    'product-bundle-builder',
    class ProductBundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.productsByCollection = {};
        this.filteredProducts = [];
        this.selectedSlots = {};
        this.activeSlotIndex = null;
        this.activeTier = null;
        this.activeTab = null;
        this.currentSize = null;
        this.isLoading = false;
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.collectionHandle = this.dataset.collectionHandle;
        this.collections = JSON.parse(this.dataset.collections || '[]');
        this.currentVariantId = this.dataset.currentVariantId;
        this.currentProductId = this.dataset.currentProductId;
        this.currentProductTitle = this.dataset.currentProductTitle;
        this.currentProductImage = this.dataset.currentProductImage;
        this.currentVariantPrice = parseInt(this.dataset.currentVariantPrice, 10);
        this.currentVariantComparePrice =
          parseInt(this.dataset.currentVariantComparePrice, 10) || 0;
        this.currentSize = this.dataset.currentSize;
        this.save1Percent = this.parseSavePercent(this.getAttribute('data-save1'), 0);
        this.save2Percent = this.parseSavePercent(this.getAttribute('data-save2'), 25);
        this.save3Percent = this.parseSavePercent(this.getAttribute('data-save3'), 30);
        this.inspiredBy = this.dataset.inspiredBy || '';

        /* MARCA | 25/08/2026 | Parse excluded sizes from schema setting */
        this.excludedSizes = (this.dataset.excludedSizes || '')
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);

        this.tiers = this.querySelectorAll('[data-tier]');
        this.dropdown = this.querySelector('[data-pbb-dropdown]');
        this.searchInput = this.querySelector('[data-pbb-search]');
        this.resultsContainer = this.querySelector('[data-pbb-results]');
        this.tabsContainer = this.querySelector('[data-pbb-tabs]');

        this.bindEvents();
        this.interceptProductForm();
        this.fetchProducts();

        /* MARCA | 25/08/2026 | Default to tier 1 on page load so 1x Bottle is always pre-selected */
        if (!this.excludedSizes.includes(this.currentSize)) {
          this.selectTier(1);
        }

        this.updatePricing();

        /* MARCA | 21/08/2026 | Subscribe to variant changes to sync size */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => this.handleVariantChange(event)
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      bindEvents() {
        this.tiers.forEach((tier) => {
          tier.querySelector('[data-tier-header]')?.addEventListener('click', () => {
            this.selectTier(parseInt(tier.dataset.tier, 10));
          });
        });

        if (this.searchInput) {
          this.searchInput.addEventListener(
            'input',
            this.debounce(() => this.handleSearch(), 250)
          );
        }

        if (this.tabsContainer) {
          this.tabsContainer.addEventListener('click', (e) => {
            const tab = e.target.closest('[data-pbb-tab]');
            if (tab) this.switchTab(tab.dataset.pbbTab);
          });
        }

        document.addEventListener('click', (e) => {
          if (this.activeSlotIndex !== null && !this.contains(e.target)) {
            this.closeDropdown();
          }
        });
      }

      /* --- Tier Selection --- */

      selectTier(tier) {
        if (this.activeTier === tier) return;

        this.activeTier = tier;
        this.selectedSlots = {};
        this.closeDropdown();

        this.tiers.forEach((el) => {
          const t = parseInt(el.dataset.tier, 10);
          const isActive = t === tier;
          el.classList.toggle('product-bundle-builder__tier--active', isActive);

          const content = el.querySelector('[data-tier-content]');
          if (content) {
            if (isActive && tier > 1) {
              this.expandContent(content);
              this.initSlots(el, tier);
            } else {
              this.collapseContent(content);
            }
          }
        });
      }

      /* MARCA | 21/08/2026 | Accordion expand/collapse for tier content */

      expandContent(el) {
        el.hidden = false;
        el.style.maxHeight = '0';
        el.classList.add('product-bundle-builder__tier-content--open');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.maxHeight = el.scrollHeight + 'px';
            setTimeout(() => {
              el.style.maxHeight = 'none';
            }, 350);
          });
        });
      }

      collapseContent(el) {
        el.classList.remove('product-bundle-builder__tier-content--open');
        const height = el.scrollHeight;
        el.style.maxHeight = height + 'px';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.maxHeight = '0';
            setTimeout(() => {
              el.hidden = true;
            }, 350);
          });
        });
      }

      /* MARCA | 21/08/2026 | Initialise fragrance slots — slot 1 pre-filled with current product */

      initSlots(tierEl, tierCount) {
        const slotsContainer = tierEl.querySelector('[data-pbb-slots]');
        if (!slotsContainer) return;

        slotsContainer.innerHTML = '';
        this.selectedSlots = {};

        for (let i = 1; i <= tierCount; i++) {
          const slot = document.createElement('div');
          slot.className = 'product-bundle-builder__slot';
          slot.dataset.slotIndex = i;

          slot.innerHTML = `
            <div class="product-bundle-builder__slot-image" data-slot-image>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
            </div>
            <div class="product-bundle-builder__slot-info">
              <span class="product-bundle-builder__slot-label" data-slot-label>Fragrance ${i}</span>
              <span class="product-bundle-builder__slot-subtitle" data-slot-subtitle>Please select an option</span>
            </div>
            <div class="product-bundle-builder__slot-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
          `;

          slot.addEventListener('click', () => this.openDropdown(i, tierEl));
          slotsContainer.appendChild(slot);
        }

        this.prefillCurrentProduct(slotsContainer);
      }

      prefillCurrentProduct(slotsContainer) {
        const sizeProducts = this.getProductsForCurrentSize();
        const currentVariant = this.findCurrentVariantInProducts(sizeProducts);

        this.selectedSlots[1] = {
          variantId: this.currentVariantId,
          title: this.currentProductTitle,
          image: this.currentProductImage,
          inspiredBy: this.inspiredBy,
          productId: this.currentProductId,
        };

        this.updateSlotUI(slotsContainer, 1);
      }

      findCurrentVariantInProducts(products) {
        for (const p of products) {
          const v = p.variants?.find((v) => String(v.id) === String(this.currentVariantId));
          if (v) return { ...p, variantId: v.id, price: v.price };
        }
        return null;
      }

      /* --- Dropdown --- */

      openDropdown(slotIndex, tierEl) {
        if (this.activeSlotIndex === slotIndex) {
          this.closeDropdown();
          return;
        }

        this.activeSlotIndex = slotIndex;
        this.activeTierEl = tierEl;

        const slotsContainer = tierEl.querySelector('[data-pbb-slots]');
        if (!slotsContainer) return;

        if (this.dropdown.parentNode !== slotsContainer.parentNode) {
          slotsContainer.parentNode.appendChild(this.dropdown);
        }

        this.dropdown.hidden = false;
        if (this.searchInput) this.searchInput.value = '';
        this.activeTab = this.collectionHandle;
        this.filteredProducts = this.getProductsForCurrentSize();
        this.renderResults();

        slotsContainer.querySelectorAll('[data-slot-index]').forEach((s) => {
          s.classList.toggle(
            'product-bundle-builder__slot--active',
            parseInt(s.dataset.slotIndex, 10) === slotIndex
          );
        });

        if (this.tabsContainer) {
          this.tabsContainer.querySelectorAll('[data-pbb-tab]').forEach((tab) => {
            tab.classList.toggle(
              'product-bundle-builder__tab--active',
              tab.dataset.pbbTab === this.activeTab
            );
          });
        }
      }

      closeDropdown() {
        this.activeSlotIndex = null;
        this.dropdown.hidden = true;

        this.querySelectorAll('[data-slot-index]').forEach((s) => {
          s.classList.remove('product-bundle-builder__slot--active');
        });
      }

      /* --- Tab switching --- */

      switchTab(handle) {
        if (this.activeTab === handle) return;
        this.activeTab = handle;

        if (this.tabsContainer) {
          this.tabsContainer.querySelectorAll('[data-pbb-tab]').forEach((tab) => {
            tab.classList.toggle(
              'product-bundle-builder__tab--active',
              tab.dataset.pbbTab === handle
            );
          });
        }

        if (this.searchInput) this.searchInput.value = '';
        this.filteredProducts = this.getProductsForCurrentSize(handle);
        this.renderResults();
      }

      /* --- Search --- */

      handleSearch() {
        const query = (this.searchInput?.value || '').toLowerCase().trim();
        if (query.length === 0) {
          this.filteredProducts = this.getProductsForCurrentSize(this.activeTab);

          if (this.tabsContainer) {
            this.tabsContainer.querySelectorAll('[data-pbb-tab]').forEach((tab) => {
              tab.classList.toggle(
                'product-bundle-builder__tab--active',
                tab.dataset.pbbTab === this.activeTab
              );
            });
          }
        } else {
          const allProducts = this.getProductsForCurrentSize(null);
          this.filteredProducts = allProducts.filter(
            (p) =>
              p.title.toLowerCase().includes(query) ||
              (p.inspiredBy || '').toLowerCase().includes(query)
          );

          if (this.tabsContainer) {
            this.tabsContainer.querySelectorAll('[data-pbb-tab]').forEach((tab) => {
              tab.classList.remove('product-bundle-builder__tab--active');
            });
          }
        }
        this.renderResults();
      }

      /* MARCA | 21/08/2026 | Get products for current size from collection data */

      getProductsForCurrentSize(collectionHandle) {
        const size = this.currentSize;
        if (!size) return [];

        const handles = collectionHandle
          ? [collectionHandle]
          : Object.keys(this.productsByCollection);

        const seen = new Set();
        const products = [];

        handles.forEach((h) => {
          const arr = this.productsByCollection[h] || [];
          arr.forEach((p) => {
            if (seen.has(p.id)) return;
            const variant = p.variants?.find(
              (v) => v.size === size && v.available
            );
            if (variant) {
              seen.add(p.id);
              products.push({
                ...p,
                variantId: variant.id,
                price: variant.price,
              });
            }
          });
        });

        return products;
      }

      /* MARCA | 21/08/2026 | Render product list rows with "Same Again?" shortcut */

      renderResults() {
        if (!this.resultsContainer) return;

        if (this.isLoading) {
          this.resultsContainer.innerHTML =
            '<div class="product-bundle-builder__loading"><span class="product-bundle-builder__spinner"></span></div>';
          return;
        }

        const alreadySelectedIds = Object.values(this.selectedSlots)
          .map((s) => String(s.variantId));

        const fragment = document.createDocumentFragment();

        const sameAgainRow = document.createElement('button');
        sameAgainRow.type = 'button';
        sameAgainRow.className =
          'product-bundle-builder__result-row product-bundle-builder__result-row--same';
        sameAgainRow.setAttribute('aria-label', 'Add same product again');
        sameAgainRow.innerHTML = `
          <span class="product-bundle-builder__result-section-label">SAME AGAIN?</span>
          <span class="product-bundle-builder__result-title">${this.escapeHtml(this.currentProductTitle)}</span>
          <span class="product-bundle-builder__result-add" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          </span>
        `;
        sameAgainRow.addEventListener('click', () => {
          this.selectProduct({
            variantId: this.currentVariantId,
            title: this.currentProductTitle,
            image: this.currentProductImage,
            inspiredBy: this.inspiredBy,
            productId: this.currentProductId,
          });
        });
        fragment.appendChild(sameAgainRow);

        if (this.filteredProducts.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'product-bundle-builder__empty';
          empty.textContent = 'No products found.';
          fragment.appendChild(empty);
        } else {
          this.filteredProducts.forEach((product) => {
            const isSelected = alreadySelectedIds.includes(String(product.variantId));
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'product-bundle-builder__result-row';
            row.setAttribute('aria-label', `Add ${product.title}`);
            if (isSelected) {
              row.classList.add('product-bundle-builder__result-row--disabled');
              row.disabled = true;
            }

            row.innerHTML = `
              <span class="product-bundle-builder__result-title">${this.escapeHtml(product.title)}</span>
              <span class="product-bundle-builder__result-inspired">${this.escapeHtml(product.inspiredBy)}</span>
              <span class="product-bundle-builder__result-add" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              </span>
            `;

            if (!isSelected) {
              row.addEventListener('click', () => {
                this.selectProduct(product);
              });
            }

            fragment.appendChild(row);
          });
        }

        this.resultsContainer.innerHTML = '';
        this.resultsContainer.appendChild(fragment);
      }

      /* --- Selection --- */

      selectProduct(product) {
        if (this.activeSlotIndex === null) return;

        this.selectedSlots[this.activeSlotIndex] = {
          variantId: String(product.variantId),
          title: product.title,
          image: product.image || '',
          inspiredBy: product.inspiredBy || '',
          productId: product.productId || product.id,
        };

        const slotsContainer = this.activeTierEl?.querySelector('[data-pbb-slots]');
        if (slotsContainer) {
          this.updateSlotUI(slotsContainer, this.activeSlotIndex);
        }

        this.closeDropdown();
      }

      updateSlotUI(slotsContainer, slotIndex) {
        const slot = slotsContainer.querySelector(`[data-slot-index="${slotIndex}"]`);
        if (!slot) return;

        const data = this.selectedSlots[slotIndex];
        const imageContainer = slot.querySelector('[data-slot-image]');
        const labelEl = slot.querySelector('[data-slot-label]');
        const subtitleEl = slot.querySelector('[data-slot-subtitle]');

        if (data) {
          slot.classList.add('product-bundle-builder__slot--filled');
          if (data.image) {
            imageContainer.innerHTML = `<img src="${this.escapeHtml(data.image)}" alt="${this.escapeHtml(data.title)}" width="43" height="43" loading="lazy">`;
          }
          labelEl.textContent = data.title;
          subtitleEl.textContent = data.inspiredBy
            ? `Inspired by ${data.inspiredBy}`
            : '';
        } else {
          slot.classList.remove('product-bundle-builder__slot--filled');
          imageContainer.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>';
          labelEl.textContent = `Fragrance ${slotIndex}`;
          subtitleEl.textContent = 'Please select an option';
        }
      }

      parseSavePercent(value, fallback) {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
      }

      /* --- Pricing --- */

      /* MARCA | 15/09/2026 | Bundle discounts are calculated from the full (compare-at)
         price so an existing product discount is not discounted a second time. */

      getBasePrice() {
        return this.currentVariantComparePrice > this.currentVariantPrice
          ? this.currentVariantComparePrice
          : this.currentVariantPrice;
      }

      /* MARCA | 21/08/2026 | Update displayed prices based on current variant price and savings % */

      updatePricing() {
        const price = this.getBasePrice();
        if (!price) return;

        const formatPrice = (pence) => {
          const pounds = (pence / 100).toFixed(2);
          return `£${pounds}`;
        };

        this.tiers.forEach((tier) => {
          const t = parseInt(tier.dataset.tier, 10);
          const priceEl = tier.querySelector('[data-tier-price]');
          const originalEl = tier.querySelector('[data-tier-original]');
          const subtitleEl = tier.querySelector('[data-tier-subtitle]');

          if (t === 1) {
            const savePercent = this.save1Percent;
            const discounted = Math.round(price * (1 - savePercent / 100));

            if (priceEl) priceEl.textContent = formatPrice(discounted);
            if (originalEl) {
              originalEl.hidden = savePercent <= 0;
              originalEl.textContent = formatPrice(price);
            }
            if (subtitleEl) {
              subtitleEl.hidden = savePercent <= 0;
              subtitleEl.textContent = `${savePercent}% off 1 bottle`;
            }
          } else if (t === 2) {
            const savePercent = this.save2Percent;
            const perBottle = Math.round(price * (1 - savePercent / 100));
            const total = perBottle * 2;
            const originalTotal = price * 2;

            if (priceEl) priceEl.textContent = formatPrice(total);
            if (originalEl) {
              originalEl.hidden = savePercent <= 0;
              originalEl.textContent = formatPrice(originalTotal);
            }
            if (subtitleEl)
              subtitleEl.textContent = `Only ${formatPrice(perBottle)} per bottle`;
          } else if (t === 3) {
            const savePercent = this.save3Percent;
            const perBottle = Math.round(price * (1 - savePercent / 100));
            const total = perBottle * 3;
            const originalTotal = price * 3;

            if (priceEl) priceEl.textContent = formatPrice(total);
            if (originalEl) {
              originalEl.hidden = savePercent <= 0;
              originalEl.textContent = formatPrice(originalTotal);
            }
            if (subtitleEl)
              subtitleEl.textContent = `Only ${formatPrice(perBottle)} per bottle`;
          }
        });
      }

      /* --- Variant Change --- */

      /* MARCA | 21/08/2026 | Handle variant change — update size, re-filter products, clear invalid slots */

      handleVariantChange(event) {
        const variant = event.data?.variant;
        if (!variant) return;

        const newSize = this.extractSize(variant.title || '');
        const oldSize = this.currentSize;

        this.currentVariantId = String(variant.id);
        this.currentVariantPrice = variant.price;
        this.currentVariantComparePrice = variant.compare_at_price || 0;

        /* MARCA | 25/08/2026 | Hide the bundle builder when switching to an excluded size */
        if (newSize && this.excludedSizes.includes(newSize)) {
          this.hidden = true;
          this.currentSize = newSize;
          this.activeTier = null;
          this.selectedSlots = {};
          this.closeDropdown();
          this.tiers.forEach((el) => {
            el.classList.remove("product-bundle-builder__tier--active");
            const content = el.querySelector("[data-tier-content]");
            if (content) {
              content.hidden = true;
              content.classList.remove(
                "product-bundle-builder__tier-content--open",
              );
              content.style.maxHeight = "0";
            }
          });
          return;
        }

        const wasHidden = this.hidden;
        this.hidden = false;

        if (newSize && newSize !== oldSize) {
          this.currentSize = newSize;
          this.clearAllSlots();
        }

        /* MARCA | 25/08/2026 | Reset to tier 1 when returning from an excluded size */
        if (wasHidden) {
          this.selectTier(1);
        }

        this.updatePricing();
        this.updateCurrentProductVariant(variant);
      }

      extractSize(variantTitle) {
        const lower = variantTitle.toLowerCase().replace(/\s+/g, '');
        const sizes = ['100ml', '50ml', '30ml', '3ml'];
        for (const size of sizes) {
          if (lower.includes(size)) return size;
        }
        return null;
      }

      updateCurrentProductVariant(variant) {
        this.currentVariantId = String(variant.id);

        if (this.activeTier > 1 && this.selectedSlots[1]) {
          const isCurrentProduct =
            String(this.selectedSlots[1].productId) === String(this.currentProductId);

          if (isCurrentProduct) {
            this.selectedSlots[1].variantId = String(variant.id);

            const activeTierEl = this.querySelector(
              `[data-tier="${this.activeTier}"]`
            );
            if (activeTierEl) {
              const slotsContainer = activeTierEl.querySelector('[data-pbb-slots]');
              if (slotsContainer) this.updateSlotUI(slotsContainer, 1);
            }
          }
        }
      }

      clearAllSlots() {
        this.selectedSlots = {};
        this.closeDropdown();

        if (this.activeTier && this.activeTier > 1) {
          const activeTierEl = this.querySelector(
            `[data-tier="${this.activeTier}"]`
          );
          if (activeTierEl) {
            this.initSlots(activeTierEl, this.activeTier);
          }
        }

      }

      /* --- Fetch Products --- */

      async fetchProducts() {
        this.isLoading = true;

        try {
          const response = await fetch('/?sections=product-bundle-products');
          if (!response.ok) throw new Error('Failed to fetch products');
          const sections = await response.json();
          const html = sections['product-bundle-products'] || '';
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const jsonStr = (doc.body.textContent || '').trim();
          const data = JSON.parse(jsonStr);

          if (Array.isArray(data)) {
            this.productsByCollection[this.collectionHandle] = data;
          } else {
            Object.keys(data).forEach((handle) => {
              this.productsByCollection[handle] = data[handle] || [];
            });
          }

          this.activeTab = this.collectionHandle;
        } catch (err) {
          console.error('[ProductBundleBuilder] Fetch error:', err);
          this.productsByCollection = {};
        } finally {
          this.isLoading = false;
        }
      }

      /* --- Product Form Interception --- */

      /* MARCA | 21/08/2026 | Hijack existing add-to-cart button when bundle tier > 1.
         Uses capture-phase listener on a common ancestor so the event is intercepted
         before the product-form's own handler fires. */

      interceptProductForm() {
        const ancestor =
          this.closest('.product__info-container') ||
          this.closest('product-info') ||
          this.parentElement;
        if (!ancestor) return;

        ancestor.addEventListener(
          'submit',
          (e) => {
            if (!this.activeTier || this.activeTier <= 1) return;

            const form = e.target.closest('form[data-type="add-to-cart-form"]');
            if (!form) return;

            e.stopPropagation();
            e.preventDefault();

            const filledCount = Object.keys(this.selectedSlots).length;
            if (filledCount < this.activeTier) {
              this.highlightEmptySlots();
              return;
            }

            this.submitBundle(form);
          },
          true
        );
      }

      highlightEmptySlots() {
        const activeTierEl = this.querySelector(
          `[data-tier="${this.activeTier}"]`
        );
        if (!activeTierEl) return;

        activeTierEl.querySelectorAll('[data-slot-index]').forEach((slot) => {
          const idx = parseInt(slot.dataset.slotIndex, 10);
          if (!this.selectedSlots[idx]) {
            slot.classList.add('product-bundle-builder__slot--error');
            setTimeout(
              () => slot.classList.remove('product-bundle-builder__slot--error'),
              1000
            );
          }
        });
      }

      async submitBundle(form) {
        const productFormEl = form?.closest('product-form');
        const submitBtn = productFormEl?.querySelector('[type="submit"]');

        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
        }

        const items = Object.values(this.selectedSlots).map((slot) => ({
          id: parseInt(slot.variantId, 10),
          quantity: 1,
        }));

        if (items.length === 0) return;

        const miniCart = document.querySelector('mini-cart');
        const body = { items };

        if (miniCart) {
          body.sections = miniCart
            .getSectionsToRender()
            .map((s) => s.id)
            .join(',');
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(
            `${theme?.routes?.cart_add_url || '/cart/add.js'}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(body),
            }
          );

          const data = await response.json();

          if (data.status && data.status !== 200) {
            console.error(
              '[ProductBundleBuilder] Cart error:',
              data.description || data.message
            );
            return;
          }

          if (miniCart) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-bundle-builder',
              cartData: data,
            });
            miniCart.renderContents(data);
          } else {
            window.location.href = theme?.routes?.cart_url || '/cart';
          }
        } catch (err) {
          console.error('[ProductBundleBuilder] Submit error:', err);
        } finally {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
          }
        }
      }

      /* --- Utilities --- */

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
      }

      debounce(fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    }
  );
}
